// @flow
import {
  update,
  append,
  without,
  dropLastWhile,
  all,
  any,
  isNil,
  adjust,
  uniq,
  equals,
} from "ramda"
import type { SplitsterClient } from "splitster"

import { get } from "common/utils/get"
import set from "common/utils/set"
import type { SearchMode } from "./SearchFormState.types"
import SearchFormState from "./SearchFormState"
import MultiSelectFilter from "./MultiSelectFilter"

const MINIMAL_PRICE = 0
const MAXIMAL_PRICE = 0 // zero means unlimited price
const MAXIMAL_DURATION = 25
const MINIMAL_DURATION = 2
const MAXIMAL_FLIGHT_DURATION = 60
const DEFAULT_DIRECT_FLIGHTS = false
const DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT = true
const DEFAULT_RETURN_TO_DIFFERENT_AIRPORT = true
const DEFAULT_NO_BAGS = true
const DEFAULT_DAY_TIMES_TYPE = "departure"
const DEFAULT_SORT_BY = "quality"
const DEFAULT_SORT_AGGREGATE_BY = "popularity"
const DEFAULT_STOP_NUMBER = -1
const SWITCHABLE_FILTERS = ["dayTimes", "daysInWeek"]
export const AVAILABLE_TRANSPORT_TYPES = ["bus", "train", "aircraft"]

export type PriceFilterType = {|
  min: number,
  max: number,
|}

export const defaultPriceFilter = {
  min: MINIMAL_PRICE,
  max: MAXIMAL_PRICE,
}

export const defaultTransportFilter = AVAILABLE_TRANSPORT_TYPES

export class TransportFilter {
  selected: string[]

  constructor(selected: string[] = defaultTransportFilter) {
    this.selected = selected
  }

  addType(type: string) {
    return new TransportFilter(uniq(append(type, this.selected)))
  }

  removeType(type: string) {
    return new TransportFilter(without([type], this.selected))
  }

  isSet() {
    return !equals(this.selected, defaultTransportFilter)
  }
}

type DayTimesFilterItem = {|
  type: "departure" | "arrival",
  min: number,
  max: number,
|}

const defaultDayTimesFilterItem = {
  type: DEFAULT_DAY_TIMES_TYPE, // departure or arrival
  min: 0,
  max: 24,
}

class DayTimesFilter {
  dayTimesList = [] // 0 = outbound, 1 = inbound, rest is multicity

  constructor(dayTimesList: DayTimesFilterItem[] = []) {
    this.dayTimesList = dayTimesList
  }

  _addMissingDirections(direction: number) {
    // inserts default data for all new directions up to the direction provided
    const dayTimesList = Array(Math.max(0, direction - this.dayTimesList.length + 1))
      .fill(null)
      // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
      .reduce(append(defaultDayTimesFilterItem), this.dayTimesList)
    return new DayTimesFilter(dayTimesList)
  }

  _isItemDefault(item: DayTimesFilterItem, ignoreType: boolean = false) {
    return (
      !item ||
      (item.min === 0 && item.max === 24 && (ignoreType || item.type === DEFAULT_DAY_TIMES_TYPE))
    )
  }

  isDefault(index: number, ignoreType: boolean = false) {
    const dayTimesFilter = this.dayTimesList[index]
    return this._isItemDefault(dayTimesFilter, ignoreType)
  }

  _removeUnnecessaryDefaults() {
    return new DayTimesFilter(dropLastWhile(item => this._isItemDefault(item), this.dayTimesList))
  }

  getDirectionValue(direction: number) {
    return this.dayTimesList[direction] || defaultDayTimesFilterItem
  }

  setDirectionValue(direction: number, value: DayTimesFilterItem) {
    const newDayTimesFilter = this._addMissingDirections(direction)
    newDayTimesFilter.dayTimesList[direction] = value
    return newDayTimesFilter._removeUnnecessaryDefaults()
  }

  setDirectionKeyValue(direction: number, valueKey: string, value: string | number) {
    const newDayTimesList = adjust(
      set([valueKey], value),
      // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
      direction,
      this._addMissingDirections(direction).dayTimesList,
    )
    return new DayTimesFilter(newDayTimesList)._removeUnnecessaryDefaults()
  }

  isSet() {
    return Boolean(this.dayTimesList.length)
  }
}

const defaultDaysInWeekItem = [true, true, true, true, true, true, true]

class DaysInWeek {
  daysInWeekList = [] // array of arrays by direction - outer is number of direction (same as dayTimes), inner is number of day 0-6, 0 is Sunday

  constructor(daysInWeekList: boolean[][] = []) {
    this.daysInWeekList = daysInWeekList
  }

  _addMissingDirections(direction: number) {
    const daysInWeekList = Array(Math.max(0, direction - this.daysInWeekList.length + 1))
      .fill(null)
      // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
      .reduce(append(defaultDaysInWeekItem), this.daysInWeekList)
    return new DaysInWeek(daysInWeekList)
  }

  _isItemDefault(item: boolean[]) {
    return !item || all(Boolean, item)
  }

  isDefault(index: number) {
    const daysInWeekFilter = this.daysInWeekList[index]
    return this._isItemDefault(daysInWeekFilter)
  }

  _removeUnnecessaryDefaults() {
    return new DaysInWeek(dropLastWhile(item => this._isItemDefault(item), this.daysInWeekList))
  }

  getDirectionValue(direction: number) {
    return this.daysInWeekList[direction] || defaultDaysInWeekItem
  }

  setDirectionValue(direction: string, value: boolean[]) {
    const directionIndex = parseInt(direction, 10)
    const newDaysInWeekFilter = this._addMissingDirections(directionIndex)
    newDaysInWeekFilter.daysInWeekList[directionIndex] = value
    return newDaysInWeekFilter._removeUnnecessaryDefaults()
  }

  setDirectionDayValue(direction: string, day: number, value: boolean) {
    const directionIndex = parseInt(direction, 10)
    if (!Number.isInteger(directionIndex)) {
      return this
    }
    const newDaysInWeekFilter = this._addMissingDirections(directionIndex)
    // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
    newDaysInWeekFilter.daysInWeekList[directionIndex] = update(
      day,
      value,
      newDaysInWeekFilter.daysInWeekList[directionIndex],
    )
    return newDaysInWeekFilter._removeUnnecessaryDefaults()
  }

  resetDirectionValue(direction: number) {
    const newDaysInWeekFilter = this._addMissingDirections(direction)
    newDaysInWeekFilter.daysInWeekList[direction] = defaultDaysInWeekItem
    return newDaysInWeekFilter._removeUnnecessaryDefaults()
  }

  isSet() {
    return Boolean(this.daysInWeekList.length)
  }
}

const defaultStopDurationFilter = {
  min: MINIMAL_DURATION,
  max: MAXIMAL_DURATION,
}

export class LocationHashtags {
  selected: string[]

  constructor(selected: string[] = []) {
    this.selected = selected
  }

  isSelected(tag: string) {
    return this.selected.includes(tag)
  }

  addTag(tag: string) {
    return new LocationHashtags([...this.selected, tag])
  }

  removeTag(tag: string) {
    return new LocationHashtags(without([tag], this.selected))
  }

  isSet() {
    return Boolean(this.selected.length)
  }
}

const groups = {
  stops: ["stopNumber"],
  price: ["price"],
  dayTimes: ["dayTimes", "daysInWeek"],
  durations: ["stopDuration", "flightDuration"],
  airlines: ["airlines"],
  stopoverAirports: ["stopoverAirports"],
  others: ["returnFromDifferentAirport", "returnToDifferentAirport"],
  bags: ["noBags"],
  locationHashtags: ["locationHashtags"],
  transport: ["transport"],
}

const defaultFiltersState = {
  stopNumber: DEFAULT_STOP_NUMBER,
  price: defaultPriceFilter,
  dayTimes: new DayTimesFilter(),
  daysInWeek: new DaysInWeek(),
  airlines: new MultiSelectFilter(),
  stopoverAirports: new MultiSelectFilter(),
  flightDuration: MAXIMAL_FLIGHT_DURATION, // number of hours of maximal flight duration, MAXIMAL_FLIGHT_DURATION is disabled
  stopDuration: defaultStopDurationFilter,
  directFlights: DEFAULT_DIRECT_FLIGHTS,
  returnFromDifferentAirport: DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT,
  returnToDifferentAirport: DEFAULT_RETURN_TO_DIFFERENT_AIRPORT,
  noBags: DEFAULT_NO_BAGS,
  sortBy: DEFAULT_SORT_BY, // quality, price, duration, date
  sortAggregateBy: DEFAULT_SORT_AGGREGATE_BY, // popularity, price
  locationHashtags: new LocationHashtags(),
  transport: new TransportFilter(),
}

class FiltersState {
  stopNumber = defaultFiltersState.stopNumber

  price = defaultFiltersState.price

  dayTimes = defaultFiltersState.dayTimes

  daysInWeek = defaultFiltersState.daysInWeek

  airlines = defaultFiltersState.airlines

  stopoverAirports = defaultFiltersState.stopoverAirports

  flightDuration = defaultFiltersState.flightDuration

  stopDuration = defaultFiltersState.stopDuration

  directFlights = defaultFiltersState.directFlights

  returnFromDifferentAirport = defaultFiltersState.returnFromDifferentAirport

  returnToDifferentAirport = defaultFiltersState.returnToDifferentAirport

  noBags = defaultFiltersState.noBags

  sortBy = defaultFiltersState.sortBy

  sortAggregateBy = defaultFiltersState.sortAggregateBy

  locationHashtags = defaultFiltersState.locationHashtags

  transport = defaultFiltersState.transport

  static MINIMAL_DURATION = MINIMAL_DURATION

  static MAXIMAL_DURATION = MAXIMAL_DURATION

  static MAXIMAL_FLIGHT_DURATION = MAXIMAL_FLIGHT_DURATION

  static DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT = DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT

  static DEFAULT_RETURN_TO_DIFFERENT_AIRPORT = DEFAULT_RETURN_TO_DIFFERENT_AIRPORT

  static DEFAULT_NO_BAGS = DEFAULT_NO_BAGS

  static DEFAULT_DAY_TIMES_TYPE = DEFAULT_DAY_TIMES_TYPE

  filters = [
    "stopNumber",
    "price",
    "dayTimes",
    "daysInWeek",
    "airlines",
    "stopoverAirports",
    "flightDuration",
    "stopDuration",
    "directFlights",
    "returnFromDifferentAirport",
    "returnToDifferentAirport",
    "noBags",
    "sortBy",
    "sortAggregateBy",
    "locationHashtags",
    "transport",
  ]

  // eslint-disable-next-line flowtype/no-weak-types
  constructor(input: Object = defaultFiltersState) {
    this.filters.forEach(key => {
      // $FlowFixMe
      this[key] = isNil(input[key]) ? defaultFiltersState[key] : input[key]
    })
  }

  isAnyFilterActive() {
    const isSet = this.isSet.bind(this)
    return any(isSet, this.filters)
  }

  get(filterName: string) {
    return get(filterName, this)
  }

  getIn(where: string[]) {
    return get(where, this)
  }

  set(filterName: string, value: any) {
    // $FlowFixMe
    return Object.assign(new FiltersState(), this, { [filterName]: value })
  }

  remove(filterName: string) {
    // $FlowFixMe
    return Object.assign(new FiltersState(), this, {
      [filterName]: defaultFiltersState[filterName],
    })
  }

  isSet(filterName: string) {
    switch (filterName) {
      case "price":
        return this.price.min !== MINIMAL_PRICE || this.price.max !== MAXIMAL_PRICE

      case "stopNumber":
        return this.stopNumber !== DEFAULT_STOP_NUMBER

      case "dayTimes":
        return this.dayTimes.isSet()

      case "daysInWeek":
        return this.daysInWeek.isSet()

      case "flightDuration":
        return this.flightDuration !== MAXIMAL_FLIGHT_DURATION

      case "stopDuration":
        return (
          this.stopDuration.min !== MINIMAL_DURATION || this.stopDuration.max !== MAXIMAL_DURATION
        )

      case "stopDurationMin":
        return this.stopDuration.min !== MINIMAL_DURATION

      case "stopDurationMax":
        return this.stopDuration.max !== MAXIMAL_DURATION

      case "airlines":
        return this.airlines.isSet()

      case "stopoverAirports":
        return this.stopoverAirports.isSet()

      case "directFlights":
        return this.directFlights !== DEFAULT_DIRECT_FLIGHTS

      case "returnFromDifferentAirport":
        return this.returnFromDifferentAirport !== DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT

      case "returnToDifferentAirport":
        return this.returnToDifferentAirport !== DEFAULT_RETURN_TO_DIFFERENT_AIRPORT

      case "noBags":
        return this.noBags !== DEFAULT_NO_BAGS

      case "sortBy":
        return this.sortBy !== DEFAULT_SORT_BY

      case "sortAggregateBy":
        return this.sortAggregateBy !== DEFAULT_SORT_AGGREGATE_BY

      case "locationHashtags":
        return this.locationHashtags.isSet()

      case "transport":
        return this.transport.isSet()

      case "others":
        return (
          this.directFlights !== DEFAULT_DIRECT_FLIGHTS ||
          this.returnFromDifferentAirport !== DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT ||
          this.returnToDifferentAirport !== DEFAULT_RETURN_TO_DIFFERENT_AIRPORT
        )

      default:
        return false
    }
  }

  isGroupSet(groupName: string) {
    if (groups[groupName]) {
      const isSet = this.isSet.bind(this)
      return any(isSet, groups[groupName])
    }
    return false
  }

  static isApplicable(
    filterName: string,
    mode: SearchMode,
    largePlace: boolean,
    anywhere: boolean,
    hiddenFeatures: boolean,
    useAggregateResults: ?boolean,
    emptyDestination: boolean,
    splitster: SplitsterClient,
    pageName?: string,
  ) {
    if (filterName === "noBags") {
      return (
        mode !== "salesman" &&
        (!splitster ||
          (splitster.get("NO_BAGS_FILTER").value === "on" &&
            splitster.get("UMBRELLA").value !== "flightsApi"))
      )
    }

    if (hiddenFeatures) {
      if (anywhere && mode === "return") {
        return filterName === "price" || filterName === "directFlights"
      }
    } else if (mode === "multicity" || mode === "salesman") {
      return filterName === "stopNumber"
    } else if (largePlace) {
      return (
        filterName === "price" ||
        filterName === "directFlights" ||
        filterName === "stopNumber" ||
        filterName === "transport"
      )
    }

    if (filterName === "returnFromDifferentAirport") {
      return mode === "return"
    }
    if (filterName === "returnToDifferentAirport") {
      return mode === "return"
    }
    if (filterName === "price" && useAggregateResults !== undefined) {
      return useAggregateResults
    }
    if (filterName === "stopoverAirports" || filterName === "airlines") {
      return !largePlace && !anywhere && !emptyDestination && pageName !== "flight"
    }
    if (filterName === "locationHashtags") {
      return largePlace
    }
    return true
  }

  isGroupApplicable(
    groupName: string,
    mode: SearchMode,
    largePlace: boolean,
    anywhere: boolean,
    hiddenFeatures: boolean,
    useAggregateResults: boolean,
    emptyDestination: boolean,
    splitster: SplitsterClient,
    pageName: string,
  ) {
    if (groups[groupName]) {
      return any(
        filterName =>
          Boolean(
            FiltersState.isApplicable(
              filterName,
              mode,
              largePlace,
              anywhere,
              hiddenFeatures,
              useAggregateResults,
              emptyDestination,
              splitster,
              pageName,
            ),
          ),
        groups[groupName],
      )
    }
    return false
  }

  resetFilter(group: string, direction?: number) {
    switch (group) {
      case "stops":
        return this.remove("stopNumber")
      case "price":
        return this.remove("price")

      case "dayTimes":
        return this.remove("dayTimes").remove("daysInWeek")

      case "daysInWeek":
        return this.set("daysInWeek", this.get("daysInWeek").resetDirectionValue(direction))

      case "durations":
        return this.remove("stopDuration").remove("flightDuration")

      case "airlines":
        return this.remove("airlines")

      case "stopoverAirports":
        return this.remove("stopoverAirports")

      case "others":
        return this.remove("returnFromDifferentAirport")
          .remove("returnToDifferentAirport")
          .remove("directFlights")

      case "bags":
        return this.remove("noBags")

      case "locationHashtags":
        return this.remove("locationHashtags")

      case "transport":
        return this.remove("transport")

      default:
        return this
    }
  }

  switchDirections(searchForm: SearchFormState) {
    let newFilters = this

    if (searchForm.getMode() === "return") {
      SWITCHABLE_FILTERS.forEach(key => {
        const filter = newFilters.get(key)

        if (filter.isDefault(0) && filter.isDefault(1)) {
          return
        }

        if (filter.preventSwitchDirections && filter.preventSwitchDirections(searchForm)) {
          return
        }

        const departureValue = filter.getDirectionValue(0)
        const returnValue = filter.getDirectionValue(1)

        newFilters = newFilters.set(
          key,
          filter.setDirectionValue(0, returnValue).setDirectionValue(1, departureValue),
        )
      })
    }

    return newFilters
  }
}

export function filtersFromPlainJS(plain: *) {
  return new FiltersState({
    price: plain.price,
    dayTimes: new DayTimesFilter(plain.dayTimes),
    daysInWeek: new DaysInWeek(plain.daysInWeek.daysInWeekList),
    airlines: new MultiSelectFilter(
      plain.airlines.selected,
      (plain.airlines.selectedAirlinesExclude: boolean),
    ),
    stopoverAirports: new MultiSelectFilter(
      plain.stopoverAirports.selected,
      plain.airlines.selectedStopoverAirportsExclude,
    ),
  })
}

function parseDayTimePart(part) {
  const [type, minDayTime, maxDayTime] = part.split("-")
  return {
    type: type === "departure" ? "departure" : "arrival",
    min: parseInt(minDayTime, 10),
    max: parseInt(maxDayTime, 10),
  }
}

export function filtersFromQuery(query: {
  priceMin?: number,
  priceMax?: number,
  dayTimes?: string,
  daysInWeek?: string,
  stopDurationMin?: number,
  stopDurationMax?: number,
  airlinesList?: string,
  selectedAirlinesExclude?: string,
  airportsList?: string,
  selectedStopoverAirportsExclude?: string,
  flightDurationMax?: number,
  directFlights?: boolean,
  returnFromDifferentAirport?: boolean,
  locationHashtags?: string,
  returnToDifferentAirport?: boolean,
  noBags?: boolean,
  sortBy?: string,
  sortAggregateBy?: string,
  stopNumber?: number,
  transport?: string,
}) {
  const params = {}
  if (query.priceMin || query.priceMax) {
    params.price = {
      min: Math.round(parseFloat(query.priceMin)) || MINIMAL_PRICE,
      max: Math.round(parseFloat(query.priceMax)) || MAXIMAL_PRICE,
    }
  }

  if (query.dayTimes) {
    const parts = query.dayTimes.split("_")
    const parsedParts = parts.map(part =>
      part === "x" ? defaultDayTimesFilterItem : parseDayTimePart(part),
    )
    params.dayTimes = new DayTimesFilter(parsedParts)
  }

  if (query.daysInWeek) {
    const disabledDates = [false, false, false, false, false, false, false]
    const mapOfDays = query.daysInWeek
      ? query.daysInWeek
          .split("-")
          .map(days =>
            days === "x"
              ? defaultDaysInWeekItem
              : days
                  .split("")
                  .reduce(
                    (acc: boolean[], day) => update(parseInt(day, 10), true, acc),
                    disabledDates,
                  ),
          )
      : []
    params.daysInWeek = new DaysInWeek(mapOfDays)
  }

  // Parse Min and Max separately
  if (query.stopDurationMin || query.stopDurationMax) {
    params.stopDuration = {
      min: parseInt(query.stopDurationMin, 10) || MINIMAL_DURATION,
      max: parseInt(query.stopDurationMax, 10) || MAXIMAL_DURATION,
    }
  }

  if (query.airlinesList || query.selectedAirlinesExclude) {
    params.airlines = new MultiSelectFilter(
      query.airlinesList ? query.airlinesList.split(",") : [],
      query.selectedAirlinesExclude ? (query.selectedAirlinesExclude: string) === "true" : true,
    )
  }

  if (query.airportsList || query.selectedStopoverAirportsExclude) {
    params.stopoverAirports = new MultiSelectFilter(
      query.airportsList ? query.airportsList.split(",") : [],
      query.selectedStopoverAirportsExclude
        ? (query.selectedStopoverAirportsExclude: string) === "true"
        : true,
    )
  }

  if (query.flightDurationMax) params.flightDuration = parseInt(query.flightDurationMax, 10)
  if (query.directFlights) params.directFlights = query.directFlights === "true"
  if (query.returnFromDifferentAirport)
    params.returnFromDifferentAirport = query.returnFromDifferentAirport === "true"
  if (query.returnToDifferentAirport)
    params.returnToDifferentAirport = query.returnToDifferentAirport === "true"
  if (query.noBags) params.noBags = query.noBags === "true"
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortAggregateBy) params.sortAggregateBy = query.sortAggregateBy
  if (query.stopNumber) params.stopNumber = query.stopNumber
  if ("transport" in query) {
    const { transport } = query
    params.transport = new TransportFilter(transport ? transport.split(",") : [])
  }
  params.locationHashtags = new LocationHashtags(
    query.locationHashtags ? query.locationHashtags.split(",") : [],
  )

  return new FiltersState(params)
}

export default FiltersState
